"use client";

import { useEffect } from "react";
import { getSessionId, isFirstVisit, readSectionMeta, sendLandingEvent } from "./track";

/**
 * Mounted once on the PUBLIC landing page (never in preview/admin). Captures:
 * - page_view on load (+ unique_session on a visitor's first ever load)
 * - section_view when a section first enters the viewport (once per load)
 * - section_click for any click inside a section
 * - cta_click when that click lands on a link or button
 * All sends are fire-and-forget and can never disrupt the page.
 */
export function LandingAnalytics({ pageId }: { pageId: string }) {
	useEffect(() => {
		getSessionId();
		if (isFirstVisit()) sendLandingEvent({ eventType: "unique_session", landingPageId: pageId });
		sendLandingEvent({ eventType: "page_view", landingPageId: pageId });

		// --- section_view: fire once per section per page load ------------------
		const seen = new Set<string>();
		const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-landing-section]"));
		let observer: IntersectionObserver | null = null;
		if ("IntersectionObserver" in window) {
			observer = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (!entry.isIntersecting) continue;
						const el = entry.target as HTMLElement;
						const key = el.dataset.sectionId || el.dataset.sectionType || "";
						if (key && seen.has(key)) {
							observer?.unobserve(el);
							continue;
						}
						if (key) seen.add(key);
						sendLandingEvent({
							eventType: "section_view",
							landingPageId: pageId,
							...readSectionMeta(el),
						});
						observer?.unobserve(el);
					}
				},
				{ threshold: 0.35 },
			);
			nodes.forEach((n) => observer?.observe(n));
		} else {
			// No observer support: count every section as viewed once, up front.
			nodes.forEach((n) =>
				sendLandingEvent({
					eventType: "section_view",
					landingPageId: pageId,
					...readSectionMeta(n),
				}),
			);
		}

		// --- section_click + cta_click ------------------------------------------
		function onClick(e: MouseEvent) {
			const target = e.target as Element | null;
			if (!target) return;
			const meta = readSectionMeta(target);
			if (!meta.sectionId && !meta.sectionType) return; // click outside any tracked section
			sendLandingEvent({ eventType: "section_click", landingPageId: pageId, ...meta });

			const interactive = target.closest<HTMLElement>("a, button, [role='button']");
			if (!interactive) return;
			const anchor = interactive.closest<HTMLAnchorElement>("a[href]");
			const label = (interactive.getAttribute("aria-label") || interactive.textContent || "")
				.replace(/\s+/g, " ")
				.trim()
				.slice(0, 120);
			sendLandingEvent({
				eventType: "cta_click",
				landingPageId: pageId,
				...meta,
				ctaId: interactive.dataset.ctaId || interactive.id || undefined,
				ctaLabel: label || undefined,
				destination: anchor?.getAttribute("href") || undefined,
			});
		}

		document.addEventListener("click", onClick, { capture: true });

		return () => {
			observer?.disconnect();
			document.removeEventListener("click", onClick, { capture: true });
		};
	}, [pageId]);

	return null;
}
